import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import Button from "../../../components/common/Button/Button";
import Modal from "../../../components/common/Modal/Modal";
import { FiDownload } from "react-icons/fi";
import { MdOutlineFileDownload } from "react-icons/md";
import PieChartGraph from "../../../components/common/Charts/PieChartGraph";
import InputSwitch from "../../../components/common/Inputs/InputSwitch/InputSwitch";
import { getApiInstance } from "../../../utils/api";
import { useZone } from "../../../context/ZoneContext";
import { FilterBar } from "../../../components/filtersNew/filter-barNew";
import { Field } from "../../../components/ui-kitNew/controlsNew";
import { createFilterState } from "../../../lib/reportNew/filter-stateNew";
import {
    useExcelExport,
    buildInformeExportFilters,
    codsFilaDe,
} from "../useExcelExport";
import {
    INDICADORES,
    QUICK_FILTERS_INDICADOR,
    mapaPaises,
    mapaTiposObra,
    normalizarFilas,
    filasVisibles,
} from "../indicadores/definiciones";
import "../cantidadesGeneradas/CantidadesGeneradas.scss";
import "./DatosValorizacion.scss";

const INDICADOR = INDICADORES.datosValorizacion;

const COLOR_CON_DATOS = "#10b981";
const COLOR_SIN_DATOS = "#9ca3af";

const UMBRAL_FRACCION_VALORIZACION = 0.005;

export default function DatosValorizacion() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [datos, setDatos] = useState([]);
    const [excluirRaeeZeroWaste, setExcluirRaeeZeroWaste] = useState(false);

    const { selectedZone } = useZone();

    const { isModalExcelOpen, openModal, closeModal, handleExportToExcel, handleExportInformeCompleto, exportando } =
        useExcelExport({
            onError: setError,
            resumenUrl: "api/Informe/CreateReportExcelDatosValorizacionResumen",
        });

    const { filterFields } = INDICADOR;

    const [filterState, setFilterState] = useState(() =>
        createFilterState(QUICK_FILTERS_INDICADOR)
    );

    const [paisesArr, setPaisesArr] = useState([]);
    const [tiposObraArr, setTiposObraArr] = useState([]);

    useEffect(() => {
        const api = getApiInstance();
        const fetchDatos = async () => {
            setLoading(true);
            try {
                const response = await api.get(INDICADOR.datosUrl());
                setDatos(INDICADOR.filasDe(response.data));
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchDatos();

        const fetchPaises = async () => {
            try {
                const response = await api.get(`/api/Paises/GetPaises`);
                setPaisesArr(
                    Array.isArray(response.data) ? response.data : []
                );
            } catch (err) {
                console.error(err);
            }
        };
        fetchPaises();

        const fetchTiposObra = async () => {
            try {
                const response = await api.get(
                    `/api/ObrasTipo/GetAllObrasTipo`
                );
                setTiposObraArr(
                    Array.isArray(response.data) ? response.data : []
                );
            } catch (err) {
                console.error(err);
            }
        };
        fetchTiposObra();
    }, []);

    const mapPaises = useMemo(() => mapaPaises(paisesArr), [paisesArr]);
    const mapTiposObra = useMemo(
        () => mapaTiposObra(tiposObraArr),
        [tiposObraArr]
    );
    const reversePaises = useMemo(
        () =>
            Object.fromEntries(
                paisesArr.map((p) => [String(p.pais), String(p.codPais)])
            ),
        [paisesArr]
    );
    const reverseTiposObra = useMemo(
        () =>
            Object.fromEntries(
                tiposObraArr.map((t) => [
                    String(t.descripcion),
                    String(t.codTipoObra),
                ])
            ),
        [tiposObraArr]
    );

    const datosNormalizados = useMemo(
        () => normalizarFilas(datos, mapPaises, mapTiposObra),
        [datos, mapPaises, mapTiposObra]
    );

    /* Cada fila es una obra. El checkbox de RAAES / Zero Waste NO quita filas:
       descuenta las toneladas de esos proyectos dentro de la obra, que es lo que
       hace la plataforma antigua. Quitando filas se sacaban obras enteras del
       recuento, porque el proyecto es del residuo y una obra puede tener varios. */
    const datosFiltrados = useMemo(
        () =>
            filasVisibles(
                INDICADOR,
                datosNormalizados,
                filterState,
                excluirRaeeZeroWaste
            ),
        [datosNormalizados, filterState, excluirRaeeZeroWaste]
    );

    const exportFilters = useMemo(() => {
        const quick = filterState.quick || {};
        const anios = Array.isArray(quick.anio) ? quick.anio : [];
        const paises = Array.isArray(quick.pais) ? quick.pais : [];
        const tipos = Array.isArray(quick.tipoObra) ? quick.tipoObra : [];
        return {
            ...buildInformeExportFilters({
                selectedAnio: anios.length ? anios[0] : "all",
                selectedPais: paises.length
                    ? reversePaises[paises[0]] ?? paises[0]
                    : "all",
                selectedTipoObra: tipos.map((t) => reverseTiposObra[t] ?? t),
                selectedZonas: quick.codZona,
            }),
            /* Lo que manda de verdad: las obras que se están viendo. Los filtros de
               arriba se quedan por compatibilidad. El checkbox va aparte porque no
               quita obras, cambia qué toneladas se suman dentro de cada una. */
            excluirRaeeZeroWaste,
            codsFila: codsFilaDe(datosFiltrados),
        };
    }, [
        filterState,
        reversePaises,
        reverseTiposObra,
        datosFiltrados,
        excluirRaeeZeroWaste,
    ]);

    const { totalObras, obrasConDatos, pctDatos } = useMemo(() => {
        let obrasConValorizacion = 0;
        const obrasTotales = datosFiltrados.length;

        datosFiltrados.forEach((obra) => {
            const pesoTotal = Number(
                excluirRaeeZeroWaste ? obra.pesoTotalSinRaeeZW : obra.pesoTotal
            ) || 0;
            const pesoCon = Number(
                excluirRaeeZeroWaste ? obra.pesoConDatosSinRaeeZW : obra.pesoConDatos
            ) || 0;
            if (pesoTotal > 0 && pesoCon / pesoTotal > UMBRAL_FRACCION_VALORIZACION) {
                obrasConValorizacion++;
            }
        });

        const porc =
            obrasTotales > 0
                ? (obrasConValorizacion / obrasTotales) * 100
                : 0;

        return {
            totalObras: obrasTotales,
            obrasConDatos: obrasConValorizacion,
            pctDatos: porc,
        };
    }, [datosFiltrados, excluirRaeeZeroWaste]);

    const datosTarta = useMemo(() => {
        const tarta = [];
        if (obrasConDatos > 0 && pctDatos > 0) {
            tarta.push({
                name: "Con datos de valorización",
                value: pctDatos,
            });
        }
        const pctSinDatos = totalObras > 0 ? 100 - pctDatos : 0;
        if (totalObras - obrasConDatos > 0 && pctSinDatos > 0) {
            tarta.push({ name: "Sin datos de valorización", value: pctSinDatos });
        }
        return tarta;
    }, [obrasConDatos, totalObras, pctDatos]);

    const renderFilters = () => (
        <div className="filters-section-common">
            <FilterBar
                rows={datosNormalizados}
                state={filterState}
                onChange={setFilterState}
                resultCount={datosFiltrados.length}
                totalCount={datosNormalizados.length}
                fields={filterFields}
                headerExtra={
                    <Field label="Excluir RAAES / Zero Waste">
                        <div
                            className="abono-toggle"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                            }}
                        >
                            <InputSwitch
                                checked={excluirRaeeZeroWaste}
                                onChange={(isOn) =>
                                    setExcluirRaeeZeroWaste(isOn)
                                }
                            />
                            <span
                                className={`abono-toggle__label ${
                                    excluirRaeeZeroWaste
                                        ? "abono-toggle__label--active"
                                        : ""
                                }`}
                            >
                                Excluír RAAES / Zero Waste
                            </span>
                        </div>
                    </Field>
                }
                excludeFields={INDICADOR.excludeFields}
            />
        </div>
    );

    return (
        <div className="datos-valorizacion-page">
            <PageLayout
                title="Informe de Datos de Valorización"
                subtitle="Número de obras con datos de valorización frente al total"
                actions={[
                    <Button
                        key="export"
                        componentType="button"
                        variant="filled"
                        startIcon={<FiDownload />}
                        onClick={openModal}
                    >
                        Exportar a excel
                    </Button>,
                ]}
            >
                <div className="tab-content-with-filters">
                    {renderFilters()}

                    <div className="cantidades-generadas-content">
                        <div className="graficas-card">
                            <div className="charts-section">
                                <div className="chart-container pie-chart-container">
                                    <div className="chart-header valorizacion-header">
                                        <div className="valorizacion-header__left">
                                            <h3>Datos de Valorización</h3>
                                            <p className="valorizacion-formula">
                                                % de obras con datos de
                                                valorización = (nº obras con
                                                residuos con datos de
                                                valorización / nº obras con
                                                residuos totales) *100
                                            </p>
                                        </div>
                                        <div className="valorizacion-header__percentage">
                                            {pctDatos.toFixed(1)}%
                                        </div>
                                    </div>

                                    <div className="chart-stats">
                                        <span>
                                            <strong>
                                                Datos de valorización:
                                            </strong>{" "}
                                            {obrasConDatos}
                                        </span>
                                        <span className="chart-stats__sep">
                                            /
                                        </span>
                                        <span>
                                            <strong>Total obras:</strong>{" "}
                                            {totalObras}
                                        </span>
                                    </div>

                                    <div className="chart-wrapper">
                                        {loading ? (
                                            <div
                                                className="informe-chart-skeleton"
                                                aria-hidden="true"
                                            >
                                                <div className="informe-chart-skeleton__donut" />
                                                <div className="informe-chart-skeleton__lines">
                                                    <div className="informe-chart-skeleton__line" />
                                                    <div className="informe-chart-skeleton__line informe-chart-skeleton__line--medium" />
                                                    <div className="informe-chart-skeleton__line informe-chart-skeleton__line--short" />
                                                </div>
                                            </div>
                                        ) : (
                                            <PieChartGraph
                                                data={datosTarta}
                                                colors={[
                                                    COLOR_CON_DATOS,
                                                    COLOR_SIN_DATOS,
                                                ]}
                                            />
                                        )}
                                    </div>

                                    <div className="segregacion-legend">
                                        <div className="segregacion-legend__item">
                                            <span
                                                className="segregacion-legend__dot"
                                                style={{
                                                    background:
                                                        COLOR_CON_DATOS,
                                                }}
                                            />
                                            <span className="segregacion-legend__label">
                                                Verde: Con datos de valorización
                                            </span>
                                            <span className="segregacion-legend__value">
                                                {obrasConDatos}
                                            </span>
                                        </div>
                                        <div className="segregacion-legend__item">
                                            <span
                                                className="segregacion-legend__dot"
                                                style={{
                                                    background: COLOR_SIN_DATOS,
                                                }}
                                            />
                                            <span className="segregacion-legend__label">
                                                Gris: Sin datos de valorización
                                            </span>
                                            <span className="segregacion-legend__value">
                                                {totalObras - obrasConDatos}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                <Modal
                    isOpen={isModalExcelOpen}
                    onClose={closeModal}
                    title="Exportar informe a Excel"
                    actions={[
                        <Button
                            key="export-resumen"
                            className="export-excel__btn export-excel__btn--primary"
                            onClick={() =>
                                handleExportToExcel("resumen", exportFilters)
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "resumen"
                                ? "Generando..."
                                : "Informe Datos de Valorización"}
                        </Button>,
                        <Button
                            key="export-completo"
                            className="export-excel__btn export-excel__btn--secondary"
                            onClick={() =>
                                handleExportInformeCompleto({ filterState, excluirRaeeZeroWaste })
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "completo"
                                ? "Generando..."
                                : "Informe Completo"}
                        </Button>,
                    ]}
                >
                    <div className="export-excel__actions"></div>
                </Modal>
            </PageLayout>
        </div>
    );
}
